<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <div v-else class="hotel-selection">
      <app-form-group label="Find hotel" class="hotel-selection__hotel-search">
        <app-select
          @input="onHotelSearchChanged"
          v-model="selected_hotel"
          :options="map_items.hotels"
          label="description"
          placeholder="Enter hotel name"
        />
      </app-form-group>
      <div
        class="accordion hotel-selection__filters"
        :class="{ 'hotel-selection__filters--open': is_filters_open }"
      >
        <button
          @click="onToggleFilters"
          :aria-expanded="is_filters_open.toString()"
          aria-controls="hotel-map-filters"
          class="accordion__header"
          :class="{ 'accordion__header--show': is_filters_open }"
          type="button"
        >
          <span class="accordion__title">Search filters</span>
        </button>
        <form
          v-if="is_filters_open"
          @submit.prevent="onCheckForm"
          id="hotel-map-filters"
          class="hotel-selection__form"
        >
          <div class="hotel-selection__main">
            <div class="hotel-selection__row hotel-selection__row--selects">
              <app-form-group label="Territory" required>
                <app-select
                  @input="onTerritoryChanged"
                  v-model="form.territory"
                  :options="location_territories_options"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Territory"
                  :error="$v.form.territory.$error"
                />
                <template #error>
                  <div v-if="$v.form.territory.$dirty && !$v.form.territory.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Region" required>
                <app-select
                  @input="onRegionChanged"
                  v-model="form.region"
                  :options="location_regions_options"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Region"
                  :error="$v.form.region.$error"
                  :readonly="!form.territory"
                />
                <template #error>
                  <div v-if="$v.form.region.$dirty && !$v.form.region.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Area">
                <app-select
                  v-model="form.area"
                  :options="location_areas_options"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Area"
                  :readonly="!form.region"
                />
              </app-form-group>
            </div>
            <div class="hotel-selection__row hotel-selection__row--dates">
              <app-form-group label="Check-in" class="form-group--no-indent" required>
                <app-date-picker
                  @input="onSetCheckIn"
                  v-model="form.check_in"
                  placeholder="Check-in"
                  :error="$v.form.check_in.$error"
                />
                <template #error>
                  <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.required">Required field</div>
                  <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.minValue">
                    Date cannot be less than the current date
                  </div>
                </template>
              </app-form-group>
              <app-form-group label="Check-out" class="form-group--no-indent" required>
                <app-date-picker
                  v-model="form.check_out"
                  placeholder="Check-out"
                  :readonly="!form.check_in"
                  :default-value="form.check_in"
                  ref="check_out"
                  :error="$v.form.check_out.$error"
                />
                <template #error>
                  <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.required">Required field</div>
                  <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.minValue">
                    Date cannot be less than the check-in date
                  </div>
                </template>
              </app-form-group>
              <app-form-group label="Nights" class="form-group--no-indent" disabled>
                <app-input :value="calcNumberOfNights" disabled />
              </app-form-group>
              <app-form-group label="Meal plan" class="form-group--no-indent" required>
                <app-select
                  v-model="form.meal_plan"
                  :reduce="item => item.code"
                  :options="meal_plans_options"
                  :clearable="false"
                  placeholder="Meal plan"
                  :error="$v.form.meal_plan.$error"
                />
                <template #error>
                  <div v-if="$v.form.meal_plan.$dirty && !$v.form.meal_plan.required">Required field</div>
                </template>
              </app-form-group>
            </div>
          </div>
          <template v-if="accomodation_types.length">
            <app-room-icon-checkboxes @change="onTypeChanged" :list="accomodation_types" ref="checkboxes" />
          </template>
          <app-cells position="end" :indent="false">
            <app-button @click.native="onFormReset" theme="dark">Reset</app-button>
            <app-button type="submit" ref="submit">Search</app-button>
          </app-cells>
        </form>
      </div>
      <l-map class="hotel-selection__map" :zoom="zoom" :center="center" ref="map">
        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
        <v-marker-cluster :options="cluster_options">
          <l-marker
            @click="onMarkerClick(item)"
            v-for="(item, index) in clusteredHotels"
            :key="`hotel_${index}`"
            :lat-lng="item.coordinates"
          >
            <l-icon
              :icon-size="[16, 16]"
              :iconUrl="require('@/assets/img/hotel-icon-map.svg')"
            >
            </l-icon>
            <l-tooltip :options="{ permanent: true, interactive: false, direction: 'bottom' }">
              <hotel-map-tooltip-content :hotel="item" />
            </l-tooltip>
          </l-marker>
          <l-marker
            v-for="(item, index) in map_items.locations"
            :key="`location_${index}`"
            :lat-lng="item.coordinates"
          >
            <l-icon
              :icon-size="[16, 16]"
              :iconUrl="require('@/assets/img/fly-icon-map.svg')"
            ></l-icon>
            <l-tooltip :options="{ permanent: true, interactive: true, direction: 'bottom' }" class="map-tooltip">
              {{ item.description }}
            </l-tooltip>
            <l-popup class="map-popup">
              <div class="map-popup__top">
                <img src="@/assets/img/airplane-small-black.svg" alt="">
                {{ item.description }}
              </div>
              <div class="map-popup__subt">Operated by: <span v-if="!item.airlines.length">no airlines</span></div>
              <ol v-if="item.airlines.length" class="map-popup__numeric">
                <li v-for="airline in item.airlines" :key="airline.id">{{ airline.name }}</li>
              </ol>
            </l-popup>
          </l-marker>
        </v-marker-cluster>
        <l-marker
          v-if="selected_hotel"
          @click="onMarkerClick(selected_hotel)"
          :lat-lng="selected_hotel.coordinates"
          :z-index-offset="1000"
        >
          <l-icon
            :icon-size="[28, 28]"
            :icon-anchor="[14, 14]"
            :iconUrl="require('@/assets/img/hotel-icon-map.svg')"
          ></l-icon>
          <l-tooltip
            :options="{
              permanent: true,
              interactive: false,
              direction: 'bottom',
              className: 'hotel-map-tooltip--selected'
            }"
          >
            <hotel-map-tooltip-content :hotel="selected_hotel" />
          </l-tooltip>
        </l-marker>
      </l-map>
    </div>
    <modal
      name="hotel-modal"
      :width="580"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="true"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('hotel-modal')" class="popup__close" type="button"></button>
        <div class="map-popup">
          <div class="map-popup__top">
            <img src="@/assets/img/house-black-small.svg" alt="">
            <div class="map-popup__title">{{ current_hotel.name }}</div>
            <div v-if="current_hotel && current_hotel.rating" class="map-popup__rating">
              <img
                v-for="(start, index) in +current_hotel.rating.toString().split('.')[0]"
                :key="`start_full_${index}`"
                src="@/assets/img/star-full.svg"
                alt=""
              >
              <img
                v-if="current_hotel.rating.toString().split('.').length > 1"
                src="@/assets/img/star-hulf.svg"
                alt=""
              >
            </div>
          </div>
          <div v-if="current_hotel" class="map-popup__price">
            <template v-if="!current_hotel.price_from || !current_hotel.price_to">
              No price for this period
            </template>
            <template v-else>
              <template v-if="current_hotel.price_from === current_hotel.price_to">
                {{ current_hotel.price_from.toLocaleString() }}$
              </template>
              <template v-else>
                {{ current_hotel.price_from.toLocaleString() }}$ - {{ current_hotel.price_to.toLocaleString() }}$ for the stay
              </template>
            </template>
          </div>
          <a :href="current_hotel.tripadvisor" target="_blank" class="map-popup__tripadvisor">
            <img src="@/assets/img/tripadvisor.svg" alt="">
            <span>TripAdvisor {{ current_hotel.tripadvisor_rating }}</span>
          </a>
          <div class="map-popup__chips">
            <span v-for="chips in current_hotel.amenities" :key="chips.code" class="map-popup__chip">
              {{ chips.name }}
            </span>
          </div>
          <div v-if="current_hotel.note" class="map-popup__description">
            <div class="map-popup__description-title">Comment:</div>
            <div v-html="current_hotel.note" class="map-popup__description-body"></div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { Icon } from 'leaflet'
import { LMap, LTileLayer, LMarker, LIcon, LTooltip, LPopup } from 'vue2-leaflet'
import VMarkerCluster from 'vue2-leaflet-markercluster'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import {
  getHotelsAccomodationTypes,
  getHotelsMealPlanTypes,
  getLocationAreas,
  getLocationRegions,
  getLocationTerritories,
  postHotelsMap
} from '@/http'
import AppRoomIconCheckboxes from '@/components/AppRoomIconCheckboxes.vue'
import HotelMapTooltipContent from './HotelMapTooltipContent.vue'
import { required } from 'vuelidate/lib/validators'

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24
const HOTEL_SEARCH_ZOOM = 10

function createInitialForm() {
  return {
    check_in: null,
    check_out: null,
    territory: null,
    region: null,
    area: null,
    meal_plan: null,
    accomodation_types: []
  }
}

export default {
  name: 'HotelsMap',
  components: {
    AppRoomIconCheckboxes,
    HotelMapTooltipContent,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    VMarkerCluster,
    LTooltip,
    LPopup
  },
  data() {
    return {
      is_loaded: false,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '',
      zoom: 7,
      center: [-2.408550, 34.385006],
      cluster_options: { disableClusteringAtZoom: 11 },
      is_filters_open: false,
      map_items: { hotels: [], locations: [] },
      selected_hotel: null,
      current_hotel: {},
      form: createInitialForm(),
      location_territories_options: [],
      location_areas_options: [],
      location_regions_options: [],
      accomodation_types: [],
      meal_plans_options: [],
    }
  },
  validations: {
    form: {
      territory: { required },
      region: { required },
      meal_plan: { required },
      check_in: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      check_out: {
        required,
        minValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form.check_in).setHours(0, 0, 0, 0)) : true
        }
      },
    }
  },
  computed: {
    clusteredHotels() {
      if (!this.selected_hotel) return this.map_items.hotels
      return this.map_items.hotels.filter(item => item !== this.selected_hotel)
    },
    calcNumberOfNights() {
      if (!this.form.check_in || !this.form.check_out) return ''

      const checkIn = new Date(this.form.check_in)
      const checkOut = new Date(this.form.check_out)
      const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / MILLISECONDS_PER_DAY)

      return Math.max(nights, 0)
    },
  },
  mounted() {
    this.initializeLeafletIcons()
    this.fetchMealPlans()
    this.fetchTerritories()
    this.fetchAccomodationTypes()
    this.fetchMapItems().then(() => { this.is_loaded = true })
  },
  methods: {
    initializeLeafletIcons() {
      delete Icon.Default.prototype._getIconUrl
      Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      })
    },
    fetchMealPlans() {
      return getHotelsMealPlanTypes()
        .then(res => { this.meal_plans_options = res.data })
    },
    fetchTerritories() {
      return getLocationTerritories()
        .then(res => { this.location_territories_options = res.data })
    },
    fetchAccomodationTypes() {
      return getHotelsAccomodationTypes()
        .then(res => { this.accomodation_types = res.data.map(item => ({ ...item, checked: false })) })
    },
    fetchMapItems() {
      return postHotelsMap(this.form)
        .then(res => {
          this.selected_hotel = null
          this.map_items = res.data
        })
    },
    onToggleFilters() {
      this.is_filters_open = !this.is_filters_open
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(this.invalidateMapSize)
        })
      })
    },
    invalidateMapSize() {
      const map = this.$refs.map && this.$refs.map.mapObject
      if (map) map.invalidateSize({ animate: false, pan: false })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      this.fetchMapItems()
        .finally(() => { this.$refs.submit.preload = false })
    },
    onTerritoryChanged() {
      this.form.region = null
      this.form.area = null
      getLocationRegions({ territory: this.form.territory })
        .then(res => { this.location_regions_options = res.data })
    },
    onMarkerClick(item) {
      this.current_hotel = item
      this.$modal.show('hotel-modal')
    },
    onHotelSearchChanged(hotel) {
      const map = this.$refs.map && this.$refs.map.mapObject
      if (!map) return

      if (hotel) {
        map.flyTo(hotel.coordinates, HOTEL_SEARCH_ZOOM, { duration: 0.8 })
      } else {
        map.flyTo(this.center, this.zoom, { duration: 0.8 })
      }
    },
    onRegionChanged() {
      this.form.area = null
      getLocationAreas({ region: this.form.region })
        .then(res => { this.location_areas_options = res.data })
    },
    onSetCheckIn() {
      this.form.check_out = null
      this.$refs.check_out.$children[0].focus()
    },
    onTypeChanged(checked) {
      this.form.accomodation_types = checked.map(item => item.code)
    },
    onFormReset() {
      this.form = createInitialForm()
      this.$v.form.$reset()
      if (this.$refs.checkboxes) this.$refs.checkboxes.resetSearchResult()
      this.fetchMapItems()
    },
  }
}
</script>

<style lang="sass">
.map-tooltip
  font-size: 13px

.leaflet-control-attribution
  display: none

.hotel-selection
  display: flex
  flex-direction: column
  height: calc(100vh - 180px)
  min-height: 0
  overflow: hidden

.hotel-selection__hotel-search
  position: relative
  z-index: 1001
  flex-shrink: 0
  width: 420px
  margin-bottom: 15px

.hotel-selection__filters
  flex-shrink: 0
  margin-bottom: 20px
  border: 1px solid #e1e1e1
  border-radius: 10px
  overflow: hidden

.hotel-selection__filters--open .accordion__header
  border-radius: 9px 9px 0 0

.hotel-selection__filters .hotel-selection__form
  margin-bottom: 0
  padding: 20px
  border-top: 1px solid #e1e1e1
  background-color: #fff

.hotel-selection .hotel-selection__map
  flex: 1 1 auto
  width: 100%
  height: auto
  min-height: 0
  overflow: hidden

.hotel-selection__map .leaflet-tile
  max-width: none !important
  max-height: none !important

.hotel-map-tooltip--selected
  border-color: #70ad14
  background-color: #f4faeb
  box-shadow: 0 1px 8px rgba(54, 92, 0, 0.25)
  color: #365c00
  font-weight: 600

.leaflet-marker-icon:focus-visible, .vue2leaflet-map
  outline: none

.leaflet-tooltip-bottom
  margin-top: 14px

.leaflet-tooltip-top:before, .leaflet-tooltip-bottom:before, .leaflet-tooltip-left:before, .leaflet-tooltip-right:before
  content: none

.leaflet-tooltip
  padding: 4px 8px 3px
  border-radius: 3px
  color: #000

.leaflet-popup-content
  margin: 13px 20px 13px 20px

.leaflet-popup-content-wrapper
  border-radius: 3px
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.15)

.leaflet-popup-tip-container
  display: none

.leaflet-container a.leaflet-popup-close-button
  color: #000
  font-weight: 600

.map-popup
  font-family: $font-family-base

.map-popup__top
  display: flex
  align-items: center
  gap: 12px
  margin-bottom: 14px
  font-size: 15px
  font-weight: 500
  line-height: 15px

  img
    margin-top: -2px

.map-popup__subt
  margin-bottom: 6px
  font-size: 14px

.map-popup__numeric
  padding-left: 15px
  list-style: numeric

.map-popup__price
  margin-bottom: 18px
  color: #000
  font-size: 14px
  line-height: 15px

.map-popup__tripadvisor
  display: flex
  align-items: center
  gap: 8px
  margin-bottom: 22px
  color: #2f8f5f
  font-size: 13px
  font-weight: 500
  line-height: 15px
  text-decoration: underline

  img
    flex-shrink: 0

.map-popup__chips
  display: flex
  align-items: center
  gap: 10px

.map-popup__chip
  padding: 6px 10px
  border-radius: 6px
  background-color: #f6f5f5
  color: #5a8119
  font-size: 12px
  line-height: 170%

.map-popup__description
  max-height: 178px
  margin-top: 30px
  padding: 10px 16px
  background-color: #f8f8f8
  overflow-y: auto
  scrollbar-color: #e1e1e1 #fff
  scrollbar-width: thin

  &::-webkit-scrollbar
    width: 5px

  &::-webkit-scrollbar-track
    background-color: #fff

  &::-webkit-scrollbar-thumb
    border-radius: 18px
    background-color: #e1e1e1

.map-popup__description-title
  margin-bottom: 5px
  color: #000
  font-size: 13px
  font-weight: 500
  line-height: 19px

.map-popup__description-body
  color: #000
  font-size: 13px
  line-height: 19px

.map-popup__rating
  display: flex
  align-items: center
  gap: 3px
</style>
